import { Injectable } from '@angular/core';
import { ICurrency, IDocument, IFaq, ILov } from '../../../../type_definitions/datatypes';
import * as _ from 'underscore';
import { FormattingService } from '../../../shared/services/formatting.service';
import { util } from '../../../shared/util';
import { Observable, Subject } from 'rxjs';
import { IDocumentType } from '../advanced/documents/bp-profiles-documents-data.service';
import { IDocumentAddModel } from '../advanced/documents/add-edit-document-dialog.component';
import { IGADeferred } from '../../../shared/services/ga-deferred.service';

export interface IProfileAttributeViewModel {
  [key:string]:IProfileAttribute[] | IProfileAttributeModel[];
}

export interface IProfileAttributeRequest {
  attributeId:number;
  attributeValue?:IProfileAttributeValue;
}

export interface IProfileAttributeResponse {
  currency?:ICurrency;
  profileAttributes:IProfileAttribute[];
}

export interface IProfileAttribute {
  id:number;
  description:string;
  unitType:string;
  valueType:string;
  sequence:number;
  code:string;
  groupCode?:string;
  requestedMandatory:boolean;
  attributeValue:IProfileAttributeValue;
  defaultRealValue:number;
  defaultBooleanValue:boolean;
  defaultCodeValue:ILov;
  comment?:string;
  codeSet:{
    id:number;
    name:string;
    description:string;
  };
  documentType?:IDocumentType;
  documentMandatory?:boolean;
  faq?:IFaq;
}

export interface IProfileAttributeValue {
  id?:number;
  agreeing?:'YES' | 'NO' | 'NA';
  comment?:string;
  codeValue?:ILov;
  realValue?:number;
  booleanValue?:boolean;
  documents?:IProfileAttributeDocument[];
}

export interface IProfileAttributeModel extends IProfileAttribute {
  showInfo?:boolean;
  listOfValues?:ILov[];
  loading?:boolean;
  countDocuments?:number;
}

export interface IProfileAttributeDocument extends IDocument {
  documentType?:IDocumentType;
  file?:File;
  isDeleted?:boolean;
  isUpdated?:boolean;
  isAdded?:boolean;
  uploading?:boolean;
  submitting?:boolean;
  formData?:IDocumentAddModel;
  uploadDeferred?:IGADeferred<void>;
}

export enum ProfileAttributeAgreeType {
  AGREE = 'YES',
  NOT_AGREE = 'NO',
  NOT_AVAILABLE = 'NA'
}

export enum ProfileAttributeValueType {
  BOOLEAN = 'BOOLEAN',
  LIST = 'LIST',
  REAL = 'REAL'
}

export enum ProfileAttributeStatusIcon {
  OK = 'fa fa-check-circle table-ok-icon',
  INVALID = 'fa fa-times-circle table-error-icon',
  WARNING = 'fa fa-exclamation-circle table-warning-icon',
  CRUCIAL = 'fa fa-exclamation-circle table-error-icon'
}

export enum ProfileAttributeCode {
  RESELLER_MARGIN = 'RESELLERMARGIN',
  STANDARD_PAYMENT_TERMS = 'STANDARDPAYTERMS',
  INVOICE_CURRENCY_EUR_FOREIGN = 'INVOICECURRENCYEUR_FOREIGN',
  INVOICE_CURRENCY_USD_FOREIGN = 'INVOICECURRENCYUSD_FOREIGN',
  CUSTOMER_CC_PAYMENT = 'CCALLOWED',
  ACSL_CC_PAYMENT = 'ACCEPTACSLCC',
  CC_MARGIN = 'CCMARGIN',
  PAYPAL_MARGIN = 'PAYPALMARGIN',
  ACSL_FREE_TAX_LOCAL = 'PAYACSLFREEVAT',
  SUPER_BP_MARGIN = 'SUPERBPMARGIN'
}

export type DocumentUrlExpression = (caveatId:number, documentId?:number) => string;

@Injectable()
export class BPProfilesAttributesService {
  private _onValidationStart:Subject<void> = new Subject<void>();
  private _onValidationSuccess:Subject<void> = new Subject<void>();
  private _onValidationError:Subject<void> = new Subject<void>();

  public onValidationStart:Observable<void> = this._onValidationStart.asObservable();
  public onValidationError:Observable<void> = this._onValidationError.asObservable();
  public onValidationSuccess:Observable<void> = this._onValidationSuccess.asObservable();

  constructor(
    private formattingService:FormattingService
  ) {
    /**/
  }

  public emitValidationStart():void {
    this._onValidationStart.next();
  }

  public emitValidationError():void {
    this._onValidationError.next();
  }

  public emitValidationSuccess():void {
    this._onValidationSuccess.next();
  }

  public getRealAttributeValue(profileAttribute:IProfileAttributeModel):number {
    // agreeing to the default: take the default
    // not agreeing to the default: take the custom value
    if (profileAttribute?.valueType !==  ProfileAttributeValueType.REAL || !profileAttribute?.attributeValue) {
      return;
    }

    return profileAttribute.attributeValue.agreeing === ProfileAttributeAgreeType.AGREE? profileAttribute.defaultRealValue : profileAttribute.attributeValue?.realValue;
  }

  public getBooleanAttributeValue(profileAttribute:IProfileAttributeModel):boolean {
    // agreeing to the default: take the default
    // not agreeing to the default: take the custom value
    if (profileAttribute?.valueType !==  ProfileAttributeValueType.BOOLEAN || !profileAttribute?.attributeValue) {
      return;
    }

    const agreeingValue:string = profileAttribute.attributeValue.agreeing;
    if (agreeingValue === ProfileAttributeAgreeType.AGREE) {
      return profileAttribute.defaultBooleanValue || true;
    } else if (agreeingValue === ProfileAttributeAgreeType.NOT_AGREE) {
      return profileAttribute.attributeValue?.booleanValue || false;
    } else {
      return;
    }
  }

  public getListAttributeValue(profileAttribute:IProfileAttributeModel):ILov {
    // agreeing to the default: take the default
    // not agreeing to the default: take the custom value
    if (profileAttribute?.valueType !==  ProfileAttributeValueType.LIST || !profileAttribute?.attributeValue) {
      return;
    }

    return profileAttribute.attributeValue.agreeing === ProfileAttributeAgreeType.AGREE? profileAttribute.defaultCodeValue : profileAttribute.attributeValue?.codeValue;
  }

  public formatAttributeValueLabel(profileAttribute:IProfileAttribute):string {
    if (profileAttribute.attributeValue.agreeing === ProfileAttributeAgreeType.NOT_AVAILABLE) {
      return 'GA.GENERAL.NA';
    } else {
      switch (profileAttribute.valueType) {
        case ProfileAttributeValueType.REAL: {
          let tcValue:number | string = this.getRealAttributeValue(profileAttribute);
          tcValue = _.isNumber(tcValue)? this.formattingService.formatDecimal(tcValue) : tcValue;
          return tcValue? `${tcValue} ${profileAttribute.unitType || ''}` : '';
        }

        case ProfileAttributeValueType.BOOLEAN:
          return this.formattingService.formatBoolean(this.getBooleanAttributeValue(profileAttribute));

        case ProfileAttributeValueType.LIST: {
          const listValue:string = this.formattingService.formatLov(this.getListAttributeValue(profileAttribute));
          return listValue? `${listValue} ${profileAttribute.unitType || ''}` : '';
        }

        default:
          return '';
      }
    }
  }

  public processModel(profileAttributes:IProfileAttribute[]):IProfileAttributeModel[] {
    return _.map(profileAttributes, this.setInitialAttributeModel);
  }

  public processTCModel(profileAttributes:IProfileAttribute[]):IProfileAttributeViewModel {
    const model:IProfileAttributeViewModel = {};
    _.each(profileAttributes, (profileAttribute:IProfileAttribute) => {
      if (!model[profileAttribute.groupCode]) {
        model[profileAttribute.groupCode] = [];
      }
      model[profileAttribute.groupCode].push(this.setInitialAttributeModel(profileAttribute));
    });
    return model;
  }

  public processCaveatsModel(profileAttributes:IProfileAttribute[]):IProfileAttributeViewModel {
    const model:IProfileAttributeViewModel = {};
    _.each(profileAttributes, (profileAttribute:IProfileAttribute) => {
      if (!model[profileAttribute.groupCode]) {
        model[profileAttribute.groupCode] = [];
      }
      model[profileAttribute.groupCode].push({
        ...this.setInitialAttributeModel(profileAttribute),
        valueType: ProfileAttributeValueType.BOOLEAN,
        countDocuments: 0
      });
    });
    return model;
  }

  private setInitialAttributeModel = (profileAttribute:IProfileAttribute):IProfileAttributeModel => {
    const attributeValue:IProfileAttributeValue = {
      agreeing: undefined,
      comment: undefined,
      codeValue: undefined,
      realValue: undefined,
      booleanValue: undefined
    };

    return {
      ...profileAttribute,
      attributeValue: util.isNullOrUndefinedOrEmpty(profileAttribute.attributeValue) ? attributeValue : profileAttribute.attributeValue,
      showInfo: false
    };
  };

  public flattenModel(model:IProfileAttributeViewModel):IProfileAttribute[] {
    return _.flatten(_.map(model, _.values));
  }

  public formatAgreeLabel(profileAttribute:IProfileAttributeModel):string {
    switch (profileAttribute.valueType) {
      case ProfileAttributeValueType.BOOLEAN:
        return 'GA.GENERAL.YES';
      case ProfileAttributeValueType.REAL:
        return `${profileAttribute.defaultRealValue} ${profileAttribute.unitType || ''}`;
      case ProfileAttributeValueType.LIST:
        return `${this.formattingService.formatLov(profileAttribute.defaultCodeValue)} ${profileAttribute.unitType || ''}`;
    }
  }

  public getStatusIcon(profileAttribute:IProfileAttributeModel):string {
    if (util.isNullOrUndefined(profileAttribute)) {
      return;
    }

    if (util.isNullOrUndefined(profileAttribute.attributeValue?.agreeing)) {
      if (profileAttribute.requestedMandatory) {
        return ProfileAttributeStatusIcon.CRUCIAL;
      } else {
        return ProfileAttributeStatusIcon.WARNING;
      }
    } else if (profileAttribute.valueType !== ProfileAttributeValueType.BOOLEAN &&
      profileAttribute.attributeValue?.agreeing === ProfileAttributeAgreeType.NOT_AGREE &&
      util.isNullOrUndefined(profileAttribute.attributeValue.realValue) &&
      util.isNullOrUndefined(profileAttribute.attributeValue.codeValue)) {
      return ProfileAttributeStatusIcon.INVALID;
    } else {
      return ProfileAttributeStatusIcon.OK;
    }
  }

  public createProfileAttributesRequestData(profileAttributes:IProfileAttribute[], originalProfileAttributes?:IProfileAttribute[]):IProfileAttributeRequest[] {
    return _.chain(util.deepClone(profileAttributes))
      .filter((profileAttribute:IProfileAttribute):boolean => {
        const originalProfileAttribute:IProfileAttribute = _.findWhere(originalProfileAttributes, { id: profileAttribute.id });
        if (util.notNullOrUndefinedOrEmpty(profileAttribute.attributeValue) || util.notNullOrUndefinedOrEmpty(originalProfileAttribute?.attributeValue)) {
          // we only interested with changed value attributes
          return !(
            _.isEqual(profileAttribute.attributeValue?.agreeing, originalProfileAttribute?.attributeValue?.agreeing) &&
            _.isEqual(profileAttribute.attributeValue?.comment, originalProfileAttribute?.attributeValue?.comment) &&
            _.isEqual(profileAttribute.attributeValue?.realValue, originalProfileAttribute?.attributeValue?.realValue) &&
            _.isEqual(profileAttribute.attributeValue?.codeValue, originalProfileAttribute?.attributeValue?.codeValue) &&
            _.isEqual(profileAttribute.attributeValue?.booleanValue, originalProfileAttribute?.attributeValue?.booleanValue) &&
            _.isEqual(profileAttribute.attributeValue?.documents, originalProfileAttribute?.attributeValue?.documents)
          );
        }
        return false;
      })
      .map((profileAttribute:IProfileAttribute):IProfileAttributeRequest => {
        delete profileAttribute.attributeValue.documents;
        return { attributeId: profileAttribute.id, attributeValue: profileAttribute.attributeValue };
      })
      .value();
  }

  public hasNewDocument(profileAttributes:IProfileAttribute[]):boolean {
    let hasNewDocument:boolean = false;
    _.each(profileAttributes, (profileAttribute:IProfileAttribute) => {
      _.each(profileAttribute.attributeValue.documents, (document:IProfileAttributeDocument) => {
        if (document.isAdded && !document.isDeleted && !document.id) {
          hasNewDocument = true;
        }
      });
    });
    return hasNewDocument;
  }
}